import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  CreditCard, Users, TrendingUp, RefreshCw, PlusCircle, X,
  AlertCircle, CheckCircle2, ExternalLink, Trash2
} from 'lucide-react';
import { API } from '../../utils/apiConfig';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const PLAN_BADGE = {
  basic:      { bg: 'rgba(99,102,241,0.15)',  fg: '#818cf8' },
  pro:        { bg: 'rgba(245,158,11,0.15)',  fg: '#fbbf24' },
  enterprise: { bg: 'rgba(168,85,247,0.18)',  fg: '#c084fc' },
};

export const AdminB2BBillingTab = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [seats, setSeats] = useState([]);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState(null);
  const [showAddSeats, setShowAddSeats] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(null); // seat_id mid-revoke

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [sub, seatsRes, plansRes] = await Promise.all([
        axios.get(`${API}/b2b/subscriptions/my`, { headers: getHeaders() }),
        axios.get(`${API}/b2b/seats`,            { headers: getHeaders() })
              .catch(() => ({ data: { seats: [] } })),
        axios.get(`${API}/b2b/plans`),
      ]);
      setSubscription(sub.data.subscription || null);
      setInvoices(sub.data.invoices || []);
      setSeats(seatsRes.data.seats || []);
      setPlans(plansRes.data.plans || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (seatId) => {
    if (!window.confirm(t('b2bBilling.revokeConfirm', 'Lizenz wirklich entziehen?'))) return;
    setBusy(seatId);
    try {
      await axios.post(`${API}/b2b/seats/unassign`,
        { seat_id: seatId },
        { headers: getHeaders() }
      );
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
    }
  };

  const handleAddSeats = async (extra) => {
    // Top-up via /<id>/checkout (renewal-checkout reuses seats_total).
    // For simplicity ask the user to upgrade plan or create new — here we
    // pop the renewal checkout (admin can pay another month at higher count
    // after re-creating in next cycle).
    if (!subscription) return;
    setBusy('add');
    try {
      const origin_url = window.location.origin;
      const res = await axios.post(
        `${API}/b2b/subscriptions/${subscription.subscription_id}/checkout`,
        { origin_url },
        { headers: getHeaders() }
      );
      if (res.data.checkout_url) {
        window.open(res.data.checkout_url, '_blank', 'noopener');
      }
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(null);
      setShowAddSeats(false);
    }
  };

  if (loading) {
    return (
      <div style={s.card} data-testid="b2b-billing-loading">
        <RefreshCw className="spin" size={18} />
        <span style={{ marginLeft: 8 }}>{t('common.loading', 'Lädt…')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...s.card, ...s.errorBox }} data-testid="b2b-billing-error">
        <AlertCircle size={18} /> {error}
      </div>
    );
  }

  // ── Empty State: keine Subscription ───────────────────────────
  if (!subscription) {
    return (
      <div data-testid="b2b-billing-empty">
        <div style={s.card}>
          <h3 style={s.h3}>{t('b2bBilling.emptyTitle', 'Noch kein Firmen-Abo')}</h3>
          <p style={s.muted}>
            {t('b2bBilling.emptyBody',
               'Lege das B2B-Abo für deine Organisation an — Plan + Sitze in 60 Sekunden.')}
          </p>
          <button
            data-testid="b2b-billing-create-btn"
            style={s.primaryBtn}
            onClick={() => setShowCreate(true)}
          >
            <PlusCircle size={16} /> {t('b2bBilling.createCta', 'Abo anlegen')}
          </button>
        </div>
        {showCreate && (
          <CreateSubscriptionModal
            plans={plans}
            onClose={() => setShowCreate(false)}
            onCreated={load}
          />
        )}
      </div>
    );
  }

  // ── Subscription vorhanden ───────────────────────────────────
  const seatsTotal = Number(subscription.seats_total || 0);
  const seatsUsed  = Number(subscription.seats_used || 0);
  const seatsAvail = Math.max(0, seatsTotal - seatsUsed);
  const utilization = seatsTotal > 0 ? Math.round((seatsUsed / seatsTotal) * 100) : 0;
  const badge = PLAN_BADGE[subscription.plan_key] || PLAN_BADGE.basic;
  const utilColor = utilization >= 90 ? '#ef4444' : utilization >= 70 ? '#fbbf24' : '#22c55e';

  return (
    <div data-testid="b2b-billing-tab">
      {/* ── Hero-Card ───────────────────────────────────────── */}
      <div style={s.heroCard}>
        <div style={s.heroHeader}>
          <div>
            <div style={s.heroLabel}>{t('b2bBilling.currentPlan', 'Aktiver Plan')}</div>
            <div style={s.heroTitle}>
              <span style={{ ...s.planBadge, background: badge.bg, color: badge.fg }}>
                {subscription.plan_name}
              </span>
              <span style={s.priceLabel}>
                {Number(subscription.seat_price_eur).toFixed(2)} € / {t('b2bBilling.seatMonth', 'Sitz/Monat')}
              </span>
            </div>
          </div>
          <button
            data-testid="b2b-billing-refresh"
            onClick={load}
            style={s.iconBtn}
            title={t('common.refresh', 'Aktualisieren')}
          >
            <RefreshCw size={16} />
          </button>
        </div>

        <div style={s.statsRow}>
          <div style={s.statCell}>
            <div style={s.statValue}>{seatsTotal}</div>
            <div style={s.statLabel}>{t('b2bBilling.seatsTotal', 'Gesamt')}</div>
          </div>
          <div style={s.statCell}>
            <div style={s.statValue}>{seatsUsed}</div>
            <div style={s.statLabel}>{t('b2bBilling.seatsUsed', 'Belegt')}</div>
          </div>
          <div style={s.statCell}>
            <div style={{ ...s.statValue, color: utilColor }}>{seatsAvail}</div>
            <div style={s.statLabel}>{t('b2bBilling.seatsFree', 'Frei')}</div>
          </div>
          <div style={s.statCell}>
            <div style={s.statValue}>
              {Number(subscription.monthly_amount_eur || 0).toFixed(2)} €
            </div>
            <div style={s.statLabel}>{t('b2bBilling.monthly', 'Monatlich')}</div>
          </div>
        </div>

        {/* Progress-Bar */}
        <div style={s.progressOuter} data-testid="b2b-billing-utilization">
          <div
            style={{
              ...s.progressInner,
              width: `${Math.min(utilization, 100)}%`,
              background: utilColor,
            }}
          />
          <span style={s.progressLabel}>{utilization}% {t('b2bBilling.utilization', 'Auslastung')}</span>
        </div>

        <button
          data-testid="b2b-billing-add-seats-btn"
          style={{ ...s.primaryBtn, marginTop: 18, width: '100%' }}
          onClick={() => setShowAddSeats(true)}
          disabled={busy === 'add'}
        >
          <PlusCircle size={16} />
          {busy === 'add'
            ? t('common.loading', 'Lädt…')
            : t('b2bBilling.addSeats', 'Mehr Sitze buchen')}
        </button>
      </div>

      {/* ── Members Table ──────────────────────────────────── */}
      <div style={s.card}>
        <div style={s.cardHead}>
          <Users size={18} />
          <h3 style={s.h3}>{t('b2bBilling.members', 'Zugewiesene Mitarbeiter')}</h3>
          <span style={s.countBadge}>{seats.length}</span>
        </div>
        {seats.length === 0 ? (
          <div style={s.muted} data-testid="b2b-billing-members-empty">
            {t('b2bBilling.membersEmpty',
               'Noch keine Sitze zugewiesen. Sag „Hey Orb, weise [Name] einen Sitz zu" im Voice-Interface.')}
          </div>
        ) : (
          <table style={s.table} data-testid="b2b-billing-members-table">
            <thead>
              <tr>
                <th style={s.th}>{t('b2bBilling.colName', 'Name')}</th>
                <th style={s.th}>{t('b2bBilling.colEmail', 'E-Mail')}</th>
                <th style={s.th}>{t('b2bBilling.colRole', 'Rolle')}</th>
                <th style={s.th}>{t('b2bBilling.colAssigned', 'Zugewiesen')}</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {seats.map((seat) => (
                <tr key={seat.seat_id} data-testid={`b2b-billing-row-${seat.seat_id}`}>
                  <td style={s.td}>{seat.user_name || '—'}</td>
                  <td style={s.td}>{seat.user_email || '—'}</td>
                  <td style={s.td}>
                    <span style={s.roleBadge}>{seat.assigned_role}</span>
                  </td>
                  <td style={s.td}>
                    {seat.assigned_at ? new Date(seat.assigned_at).toLocaleDateString() : '—'}
                  </td>
                  <td style={s.td}>
                    <button
                      data-testid={`b2b-billing-revoke-${seat.seat_id}`}
                      style={s.dangerBtn}
                      disabled={busy === seat.seat_id}
                      onClick={() => handleRevoke(seat.seat_id)}
                    >
                      <Trash2 size={14} />
                      {busy === seat.seat_id
                        ? t('common.loading', '…')
                        : t('b2bBilling.revoke', 'Lizenz entziehen')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Invoices ───────────────────────────────────────── */}
      <div style={s.card}>
        <div style={s.cardHead}>
          <TrendingUp size={18} />
          <h3 style={s.h3}>{t('b2bBilling.invoices', 'Rechnungen')}</h3>
          <span style={s.countBadge}>{invoices.length}</span>
        </div>
        {invoices.length === 0 ? (
          <div style={s.muted}>{t('b2bBilling.invoicesEmpty', 'Noch keine Rechnungen.')}</div>
        ) : (
          <table style={s.table} data-testid="b2b-billing-invoices-table">
            <thead>
              <tr>
                <th style={s.th}>{t('b2bBilling.colDate', 'Datum')}</th>
                <th style={s.th}>{t('b2bBilling.colAmount', 'Betrag')}</th>
                <th style={s.th}>{t('b2bBilling.colSeats', 'Sitze')}</th>
                <th style={s.th}>{t('b2bBilling.colStatus', 'Status')}</th>
                <th style={s.th}>{t('b2bBilling.colInvoice', 'Beleg')}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} data-testid={`b2b-billing-invoice-${inv.id}`}>
                  <td style={s.td}>
                    {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td style={s.td}>{Number(inv.amount_eur).toFixed(2)} €</td>
                  <td style={s.td}>{inv.seats_billed}</td>
                  <td style={s.td}>
                    <StatusPill status={inv.status} />
                  </td>
                  <td style={s.td}>
                    {inv.hosted_invoice_url ? (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer"
                         style={s.link}>
                        <ExternalLink size={14} /> {t('b2bBilling.viewPdf', 'Öffnen')}
                      </a>
                    ) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Add-Seats Modal (Renewal-Checkout) ──────────────── */}
      {showAddSeats && (
        <div style={s.backdrop} onClick={() => setShowAddSeats(false)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHead}>
              <h3 style={s.h3}>{t('b2bBilling.addSeatsTitle', 'Sitze nachbuchen')}</h3>
              <button style={s.iconBtn} onClick={() => setShowAddSeats(false)}>
                <X size={16} />
              </button>
            </div>
            <p style={s.muted}>
              {t('b2bBilling.addSeatsBody',
                 'Renewal-Checkout für die kommende Periode. Du wirst zu Stripe weitergeleitet.')}
            </p>
            <button
              data-testid="b2b-billing-renewal-confirm"
              style={s.primaryBtn}
              disabled={busy === 'add'}
              onClick={() => handleAddSeats()}
            >
              {busy === 'add'
                ? t('common.loading', 'Lädt…')
                : t('b2bBilling.proceedCheckout', 'Zur Stripe-Kasse')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Sub-components ───────────────────────────────────────────────
const StatusPill = ({ status }) => {
  const map = {
    paid:     { bg: 'rgba(34,197,94,0.15)',  fg: '#22c55e' },
    pending:  { bg: 'rgba(234,179,8,0.15)',  fg: '#eab308' },
    failed:   { bg: 'rgba(239,68,68,0.15)',  fg: '#ef4444' },
    refunded: { bg: 'rgba(148,163,184,0.15)', fg: '#94a3b8' },
    void:     { bg: 'rgba(148,163,184,0.15)', fg: '#94a3b8' },
  };
  const c = map[status] || map.pending;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: 10, background: c.bg, color: c.fg,
      fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4
    }}>
      {status}
    </span>
  );
};

const CreateSubscriptionModal = ({ plans, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [planKey, setPlanKey] = useState('pro');
  const [seats, setSeats] = useState(5);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const plan = plans.find((p) => p.plan_key === planKey);

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/b2b/subscriptions/create`, {
        plan_key: planKey,
        seats_total: Number(seats),
        origin_url: window.location.origin,
      }, { headers: getHeaders() });
      if (res.data.checkout_url) {
        window.open(res.data.checkout_url, '_blank', 'noopener');
      }
      onCreated();
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={s.backdrop} onClick={onClose}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHead}>
          <h3 style={s.h3}>{t('b2bBilling.createTitle', 'B2B-Abo anlegen')}</h3>
          <button style={s.iconBtn} onClick={onClose}><X size={16} /></button>
        </div>

        <label style={s.label}>{t('b2bBilling.choosePlan', 'Plan wählen')}</label>
        <div style={s.planRow} data-testid="b2b-billing-plan-selector">
          {plans.map((p) => (
            <button
              key={p.plan_key}
              data-testid={`b2b-billing-plan-${p.plan_key}`}
              onClick={() => setPlanKey(p.plan_key)}
              style={{
                ...s.planChip,
                ...(planKey === p.plan_key ? s.planChipActive : {}),
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 14 }}>{p.name}</div>
              <div style={{ fontSize: 11, opacity: 0.75 }}>
                {Number(p.seat_price_eur).toFixed(2)} € / {t('b2bBilling.seat', 'Sitz')}
              </div>
            </button>
          ))}
        </div>

        <label style={s.label}>{t('b2bBilling.seatsCount', 'Anzahl Sitze')}</label>
        <input
          data-testid="b2b-billing-seats-input"
          type="number" min="1" max={plan?.max_seats || 999}
          value={seats} onChange={(e) => setSeats(e.target.value)}
          style={s.input}
        />
        {plan?.max_seats && (
          <div style={s.muted}>
            {t('b2bBilling.maxSeatsHint', 'Max. {{n}} Sitze in diesem Plan', { n: plan.max_seats })}
          </div>
        )}

        <div style={s.summaryRow}>
          <span>{t('b2bBilling.monthly', 'Monatlich')}:</span>
          <strong>
            {plan ? (Number(plan.seat_price_eur) * Number(seats || 0)).toFixed(2) : '0.00'} €
          </strong>
        </div>

        {err && (
          <div style={s.errorBox} data-testid="b2b-billing-create-error">
            <AlertCircle size={14} /> {err}
          </div>
        )}

        <button
          data-testid="b2b-billing-create-submit"
          style={{ ...s.primaryBtn, width: '100%', marginTop: 10 }}
          disabled={busy}
          onClick={submit}
        >
          {busy
            ? t('common.loading', 'Lädt…')
            : <><CheckCircle2 size={16} /> {t('b2bBilling.confirm', 'Abo anlegen & zur Kasse')}</>
          }
        </button>
      </div>
    </div>
  );
};

// ── Styles (inline für Konsistenz mit bestehenden Tabs) ──────────
const s = {
  card: {
    background: 'rgba(15,23,42,0.6)',
    backdropFilter: 'blur(22px)',
    border: '1px solid rgba(148,163,184,0.18)',
    borderRadius: 18, padding: 22, marginBottom: 16, color: '#e2e8f0',
  },
  heroCard: {
    background: 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(168,85,247,0.10))',
    border: '1px solid rgba(148,163,184,0.22)', borderRadius: 22,
    padding: 26, marginBottom: 18, color: '#e2e8f0',
  },
  heroHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  heroLabel: { fontSize: 12, opacity: 0.65, textTransform: 'uppercase', letterSpacing: 1.2 },
  heroTitle: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 },
  planBadge: {
    padding: '6px 14px', borderRadius: 10, fontWeight: 700, fontSize: 18,
  },
  priceLabel: { fontSize: 14, opacity: 0.85 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginTop: 22 },
  statCell: {
    background: 'rgba(0,0,0,0.25)', borderRadius: 14, padding: 14, textAlign: 'center',
  },
  statValue: { fontSize: 26, fontWeight: 700 },
  statLabel: { fontSize: 11, opacity: 0.65, textTransform: 'uppercase', letterSpacing: 0.8 },
  progressOuter: {
    position: 'relative', marginTop: 18, height: 24,
    background: 'rgba(255,255,255,0.06)', borderRadius: 12, overflow: 'hidden',
  },
  progressInner: {
    height: '100%', transition: 'width .4s ease',
  },
  progressLabel: {
    position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
    justifyContent: 'center', fontSize: 12, fontWeight: 600, color: '#fff',
    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 8,
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: '#fff', border: 'none', padding: '12px 22px', borderRadius: 12,
    fontWeight: 600, cursor: 'pointer', fontSize: 14,
    justifyContent: 'center',
  },
  dangerBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: 'rgba(239,68,68,0.12)', color: '#fca5a5',
    border: '1px solid rgba(239,68,68,0.3)', padding: '6px 12px',
    borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  iconBtn: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(148,163,184,0.18)',
    color: '#e2e8f0', borderRadius: 10, padding: 8, cursor: 'pointer',
  },
  cardHead: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
  h3: { fontSize: 16, margin: 0, fontWeight: 600 },
  muted: { fontSize: 13, opacity: 0.65 },
  countBadge: {
    marginLeft: 'auto', background: 'rgba(99,102,241,0.18)', color: '#a5b4fc',
    fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 10,
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', fontSize: 11, opacity: 0.65, textTransform: 'uppercase',
    letterSpacing: 0.5, padding: '8px 6px', borderBottom: '1px solid rgba(148,163,184,0.15)',
  },
  td: {
    padding: '10px 6px', fontSize: 13,
    borderBottom: '1px solid rgba(148,163,184,0.08)',
  },
  roleBadge: {
    background: 'rgba(34,197,94,0.12)', color: '#86efac',
    fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 8,
  },
  link: { color: '#a5b4fc', display: 'inline-flex', alignItems: 'center', gap: 4 },
  errorBox: {
    background: 'rgba(239,68,68,0.1)', color: '#fca5a5',
    border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10,
    padding: 10, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
    marginTop: 10,
  },
  backdrop: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  },
  modal: {
    background: '#0f172a', border: '1px solid rgba(148,163,184,0.22)',
    borderRadius: 18, padding: 26, width: 'min(480px, 92vw)', color: '#e2e8f0',
  },
  modalHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 },
  label: { display: 'block', fontSize: 12, opacity: 0.7, marginTop: 14, marginBottom: 6 },
  planRow: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 },
  planChip: {
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(148,163,184,0.18)',
    color: '#e2e8f0', borderRadius: 12, padding: '10px 8px', cursor: 'pointer',
    transition: 'all .2s', textAlign: 'center',
  },
  planChipActive: {
    background: 'rgba(99,102,241,0.18)', borderColor: '#818cf8',
    boxShadow: '0 0 0 1px #818cf8',
  },
  input: {
    width: '100%', padding: '10px 12px', background: 'rgba(0,0,0,0.3)',
    border: '1px solid rgba(148,163,184,0.22)', borderRadius: 10,
    color: '#e2e8f0', fontSize: 14, boxSizing: 'border-box',
  },
  summaryRow: {
    display: 'flex', justifyContent: 'space-between', marginTop: 16,
    padding: '12px 14px', background: 'rgba(0,0,0,0.25)', borderRadius: 10,
    fontSize: 14,
  },
};
